import { useCallback, useEffect, useRef } from "react";

/**
 * Browser-native notifications for merchant / rider.
 * Shows an alert when a new relevant order appears.
 */
export function useBrowserNotifications() {
  const permissionRef = useRef(typeof Notification !== "undefined" ? Notification.permission : "default");

  const request = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported";
    if (Notification.permission === "granted") { permissionRef.current = "granted"; return "granted"; }
    if (Notification.permission === "denied") { permissionRef.current = "denied"; return "denied"; }
    const p = await Notification.requestPermission();
    permissionRef.current = p;
    return p;
  }, []);

  const notify = useCallback((title, body, tag) => {
    try {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      new Notification(title, { body, tag, icon: "/favicon.ico", silent: false });
    } catch (e) {
      console.error("Notification failed:", e);
    }
  }, []);

  return { request, notify, permission: permissionRef.current };
}

/**
 * Detect new items by order_id and fire notifications for additions.
 */
export function useNewItemsNotifier(items, { title, buildBody, tag }, notify) {
  const seen = useRef(new Set());
  const warmed = useRef(false);
  useEffect(() => {
    if (!items) return;
    if (!warmed.current) {
      items.forEach((o) => seen.current.add(o.order_id));
      warmed.current = true;
      return;
    }
    items.forEach((o) => {
      if (!seen.current.has(o.order_id)) {
        seen.current.add(o.order_id);
        notify?.(title, buildBody(o), `${tag}-${o.order_id}`);
      }
    });
  }, [items, title, buildBody, tag, notify]);
}
